import { DateTime } from 'luxon';

export const toISODate = (date: DateTime) => {
  return date.startOf('day').startOf('second').toISO({ suppressMilliseconds: true }) as string;
};

export const toISODateEndOfDay = (date: DateTime) => {
  return date.endOf('day').startOf('second').toISO({ suppressMilliseconds: true }) as string;
};

export const getNextYear = () => {
  const nextYear = DateTime.fromJSDate(new Date()).plus({ years: 1 });
  return toISODateEndOfDay(nextYear);
};

export const getNextYearDateOnly = () => {
  const nextYear = DateTime.fromJSDate(new Date()).plus({ years: 1 });
  return nextYear.toFormat('yyyy-MM-dd');
};

/**
 * Derives the original timezone offset from a UTC timestamp that was created at midnight in that timezone.
 * Since we always create dates at midnight (00:00 or 23:59), we can calculate the original timezone
 * by looking at the time component in UTC.
 *
 * LIMITATION: Assumes all timezones are within GMT-12 to GMT+12. Timezones beyond this range
 * (like GMT+13, GMT+14) may be incorrectly calculated due to mathematical ambiguity.
 *
 * @param isoString - ISO 8601 string in UTC (e.g., "2026-01-29T18:30:00+00:00")
 * @param isEndOfDay - If true, assumes the time was set to 23:59:59, otherwise 00:00:00
 * @returns Object with timezone offset string and offset in minutes
 */
export const deriveCreationTimezoneOffset = (
  isoString: string,
  isEndOfDay = false,
): { offsetString: string; offsetMinutes: number } => {
  const dt = DateTime.fromISO(isoString, { zone: 'utc' });

  // Get the time component in minutes from start of day (ignoring seconds for this calculation)
  const minutesFromMidnight = dt.hour * 60 + dt.minute;

  let offsetMinutes: number;
  if (isEndOfDay) {
    // For end of day (23:59), calculate offset
    // Examples:
    // - UTC shows 18:29:59, original was 23:59:59 in GMT+5:30 → offset = 330 min = +5:30
    // - UTC shows 05:59:59, original was 23:59:59 in GMT-6:00 → offset = -360 min = -6:00
    // - UTC shows 11:59:59, original was 23:59:59 in GMT+12:00 → offset = 720 min = +12:00
    const endOfDayMinutes = 23 * 60 + 59;
    offsetMinutes = endOfDayMinutes - minutesFromMidnight;

    // If the offset would be > 12 hours, it means we wrapped to next day (negative timezone)
    if (offsetMinutes > 12 * 60) {
      offsetMinutes = offsetMinutes - 24 * 60;
    }
  } else {
    // For start of day (00:00), calculate offset
    // Examples:
    // - UTC shows 18:30:00, original was 00:00:00 in GMT+5:30 → offset = 330 min = +5:30
    // - UTC shows 06:00:00, original was 00:00:00 in GMT-6:00 → offset = -360 min = -6:00
    // - UTC shows 12:00:00, original was 00:00:00 in GMT+12:00 → offset = 720 min = +12:00
    offsetMinutes = -minutesFromMidnight;

    // If the offset would be < -12 hours, it means we wrapped to previous day (positive timezone)
    // Note: We use < instead of <= to include GMT+12 as positive
    if (offsetMinutes < -12 * 60) {
      offsetMinutes = offsetMinutes + 24 * 60;
    }
    // Special case: exactly -12 hours could be either GMT+12 or GMT-12
    // We assume GMT+12 (positive) for consistency
    if (offsetMinutes === -12 * 60) {
      offsetMinutes = 12 * 60;
    }
  }

  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  return {
    offsetString: `GMT${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    offsetMinutes,
  };
};
